// static/table_engine_src/format/format_cells.js
import { colScale, isNumericCol } from "./format_core.js";
import { formatNumber, parseNumber } from "./format_numbers.js";
import { clearRawDatasets, getAnyRawDataset } from "./format_raw.js";

/**
 * Installs numeric cell format helpers into ctx.
 *
 * ctx requirements:
 *  - ctx.effectiveFormatForCol(col)
 *  - ctx.effectiveFormatForEl(el, col)
 *
 * Optional (only for reformatAllNumeric):
 *  - ctx.getRowEls()
 *  - ctx.getInput(tr, key)
 *  - ctx.getOut(tr, key)
 *  - ctx.cols
 */
export function installFormatCells(ctx) {
  if (!ctx) return;

  const effectiveFormatForCol = ctx.effectiveFormatForCol;
  const effectiveFormatForEl = ctx.effectiveFormatForEl;

  if (typeof effectiveFormatForCol !== "function" || typeof effectiveFormatForEl !== "function") {
    throw new Error("installFormatCells(ctx): ctx.effectiveFormatForCol/El must be installed first");
  }

  function resolveColForEl(el, col) {
    if (col && typeof col === "object") return col;
    if (!el) return null;

    const key =
      el.dataset?.field ||
      el.dataset?.colKey ||
      el.closest?.("td[data-col-key]")?.dataset?.colKey ||
      null;

    if (key && ctx.colByKey?.get) {
      const byKey = ctx.colByKey.get(key);
      if (byKey) return byKey;
    }

    const td = el.closest?.("td[data-col-idx]");
    const c = parseInt(td?.dataset?.colIdx || "-1", 10);
    if (Number.isInteger(c) && Array.isArray(ctx.cols) && c >= 0 && c < ctx.cols.length) {
      return ctx.cols[c] || null;
    }

    return null;
  }

  function formatForInputElement(el, fmt) {
    if (!el || !fmt || typeof fmt !== "object") return fmt;
    // Native number inputs do not allow grouped strings and require dot decimals.
    if (el.tagName === "INPUT" && String(el.type || "").toLowerCase() === "number") {
      return { ...fmt, group: false, thousands: false, locale: "", decimalSep: ".", groupSep: "," };
    }
    return fmt;
  }

  function setNumericInputValue(el, col, rawValue) {
    if (!el) return;

    const colResolved = resolveColForEl(el, col);
    const fmtCanon = colResolved ? effectiveFormatForCol(colResolved) : null;

    // rawValue is CANONICAL (unscaled). Display applies col.scale.
    const s = rawValue === null || rawValue === undefined ? "" : String(rawValue).trim();
    if (s === "") {
      el.value = "";
      clearRawDatasets(el);
      return;
    }

    let nCanon = parseNumber(s, NaN);
    if (!Number.isFinite(nCanon) && fmtCanon) {
      nCanon = parseNumber(s, NaN, fmtCanon);
    }
    if (!Number.isFinite(nCanon)) {
      // keep as typed, but clear raw (invalid)
      el.value = s;
      clearRawDatasets(el);
      return;
    }

    // store canonical precision
    el.dataset.raw = String(nCanon);

    // format DISPLAY = canonical * scale
    const fmt = formatForInputElement(el, effectiveFormatForEl(el, colResolved));
    const sc = colScale(colResolved);
    el.value = formatNumber(nCanon * sc, fmt);

    // only fill defaults if caller did not set explicit per-cell hints
    if (el.dataset.dp == null || el.dataset.dp === "") el.dataset.dp = String(fmt.dp);
    if (el.dataset.int == null || el.dataset.int === "") el.dataset.int = fmt.int ? "1" : "0";
    if (el.dataset.strip == null || el.dataset.strip === "") el.dataset.strip = fmt.strip ? "1" : "0";

    // mark engine-generated hints
    if (el.dataset.teCellFmt !== "1") el.dataset.teAutoFormat = "1";
  }

  function reformatNumericInput(el, col) {
    if (!el) return;

    const colResolved = resolveColForEl(el, col);
    const sc = colScale(colResolved);

    // dataset.raw is CANONICAL (unscaled). If missing, parse DISPLAY and unscale.
    const raw = String(getAnyRawDataset(el) ?? "").trim();
    const s = raw !== "" ? raw : String(el.value ?? "").trim();
    if (s === "") return;

    let nCanon = NaN;

    if (raw !== "") {
      const n0 = parseNumber(raw, NaN);
      if (!Number.isFinite(n0)) return;
      nCanon = n0;
    } else {
      const fmt = effectiveFormatForCol(colResolved);
      const nDisp = parseNumber(s, NaN, fmt);
      if (!Number.isFinite(nDisp)) return;
      nCanon = nDisp / sc;
    }

    el.dataset.raw = String(nCanon);

    const fmt = formatForInputElement(el, effectiveFormatForEl(el, colResolved));
    el.value = formatNumber(nCanon * sc, fmt);

    // do NOT clobber explicit per-cell hints; only fill missing
    if (el.dataset.dp == null || el.dataset.dp === "") el.dataset.dp = String(fmt.dp);
    if (el.dataset.int == null || el.dataset.int === "") el.dataset.int = fmt.int ? "1" : "0";
    if (el.dataset.strip == null || el.dataset.strip === "") el.dataset.strip = fmt.strip ? "1" : "0";
    if (el.dataset.teCellFmt !== "1") el.dataset.teAutoFormat = "1";
  }

  function setNumericOutputValue(el, col, rawValue) {
    if (!el) return;

    // rawValue is CANONICAL (unscaled). Display applies col.scale.
    const fmt = effectiveFormatForCol(col);
    let nCanon = parseNumber(rawValue, NaN);
    if (!Number.isFinite(nCanon)) {
      nCanon = parseNumber(rawValue, NaN, fmt);
    }
    if (!Number.isFinite(nCanon)) {
      el.textContent = rawValue == null ? "" : String(rawValue);
      clearRawDatasets(el);
      return;
    }

    el.dataset.raw = String(nCanon);

    const sc = colScale(col);
    el.textContent = formatNumber(nCanon * sc, fmt);

    // debug / tooling
    el.dataset.dp = String(fmt.dp);
    el.dataset.int = fmt.int ? "1" : "0";
    el.dataset.strip = fmt.strip ? "1" : "0";
  }

  function reformatNumericOutput(el, col) {
    if (!el) return;

    // Prefer canonical raw (unscaled)
    let raw = el.dataset && el.dataset.raw != null ? String(el.dataset.raw).trim() : "";

    if (raw === "") {
      // Fallback: parse display and unscale once, then cache raw
      const txt = String(el.textContent ?? "").trim();
      if (txt === "" || txt === "N/A") return;

      const fmt = effectiveFormatForCol(col);
      const sc = colScale(col);
      const nDisp = parseNumber(txt, NaN, fmt);
      if (!Number.isFinite(nDisp)) return;

      const nCanon = nDisp / sc;
      el.dataset.raw = String(nCanon);
      raw = el.dataset.raw;
    }

    const fmt = effectiveFormatForCol(col);
    const nCanon = parseNumber(raw, NaN);
    if (!Number.isFinite(nCanon)) return;

    const sc = colScale(col);
    el.textContent = formatNumber(nCanon * sc, fmt);

    el.dataset.dp = String(fmt.dp);
    el.dataset.int = fmt.int ? "1" : "0";
    el.dataset.strip = fmt.strip ? "1" : "0";
  }

  function reformatAllNumeric() {
    const getRowEls = ctx.getRowEls;
    const getInput = ctx.getInput;
    const getOut = ctx.getOut;
    const cols = ctx.cols;

    if (typeof getRowEls !== "function") return;
    if (typeof getInput !== "function") return;
    if (typeof getOut !== "function") return;
    if (!Array.isArray(cols)) return;

    const rows = getRowEls();
    for (const tr of rows) {
      for (const col of cols) {
        if (!col || !col.key) continue;
        if (!isNumericCol(col)) continue;

        if (col.kind === "input") {
          const el = getInput(tr, col.key);
          if (el) reformatNumericInput(el, col);
        } else if (col.kind === "output") {
          const el = getOut(tr, col.key);
          if (el) reformatNumericOutput(el, col);
        }
      }
    }
  }

  Object.assign(ctx, {
    setNumericInputValue,
    reformatNumericInput,
    setNumericOutputValue,
    reformatNumericOutput,
    reformatAllNumeric,
  });
}
